import React, { useCallback, useEffect, useRef, useState } from "react";
import { Table } from "../../../components/shared";
import useSearchAndFilter from "../../../hooks/useSearchAndFilter";
import FilterSideMenu from "../../../components/shared/FilterSideMenu";
import { FilterIcon, FilterIconWhite } from "../../../assets";
import { Badge } from "@mui/material";
import { Loader } from "../../../components/loaders/loaders";
import { columns } from "./types/FscLocationTypes";
import { GET_FSC_LOCATION_CONFIG, UPDATE_FSC_LOCATION_PRICE } from "../../../apis/end-points";
import { DEFAULT_PAGE_SIZE } from "../../../utils/constants";
import { putApi } from "../../../apis/api";
import { useMessage } from "../../../context/MessageContext";
import {
  GENERAL_ERROR_MESSAGE,
  SERVICE_CONFIG_MESSAGES,
} from "../../../utils/message";
import ConfirmModal from "../../../components/modals/ConfirmModal";
import useFormChangeHandler from "../../../hooks/useFormChangeHandler";

const validatePrice = (value: string): string => {
  if (value === "") return "";
  const numericRegex = /^[0-9]*\.?[0-9]*$/;
  if (!numericRegex.test(value)) return "Price must be a numeric value.";
  if (parseFloat(value) < 0) return "Price cannot be negative.";
  const decimalPart = value.split(".")[1];
  if (decimalPart && decimalPart.length > 3)
    return "Price can have maximum 3 decimal places.";
  return "";
};

const InlineEditCell: React.FC<{
  row: any;
  onPriceChange: (id: string, newPrice: number | null) => void;
  onStartEdit?: () => void;
}> = ({ row, onPriceChange, onStartEdit }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [editError, setEditError] = useState("");
  const [localPrice, setLocalPrice] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const currentPrice = localPrice !== null ? localPrice : (row.rawPrice ?? 0);

  const startEdit = () => {
    onStartEdit?.();
    const formatted = parseFloat(String(currentPrice)).toFixed(3);
    setEditValue(formatted);
    setEditError("");
    setEditing(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const value = e.target.value;

  if (value.startsWith("-")) return;

  setEditValue(value);

  if (value === "") {
    setEditError("");
    setLocalPrice(null);
    onPriceChange(row.id, null);
    return;
  }

  const error = validatePrice(value);
  setEditError(error);

  if (!error) {
    const newPrice = parseFloat(value);
    const originalFormatted = parseFloat(String(row.rawPrice ?? 0)).toFixed(3);

    if (value !== originalFormatted) {
      setLocalPrice(newPrice);
      onPriceChange(row.id, newPrice);
    } else {
      setLocalPrice(null);
      onPriceChange(row.id, null);
    }
  }
};

  const handleSubmit = () => {
    if (editError) return;
    setEditing(false);
    setEditError("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === "Escape") {
      setEditing(false);
      setEditError("");
      setLocalPrice(null);
      onPriceChange(row.id, null);
    }
  };

  const handleBlur = () => {
    if (editValue === "") {
      setEditing(false);
      setEditError("");
      return;
    }
    handleSubmit();
  };

  const displayPrice = localPrice !== null
    ? `$${localPrice.toFixed(3)}`
    : row.displayPrice;

  if (editing) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          type="text"
          className={`form__input form__input--sm form__input--active${editError ? " input--error" : ""}`}
          value={editValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
        />
        {editError && <span className="form__error">{editError}</span>}
      </div>
    );
  }

  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
        startEdit();
      }}
      style={{ cursor: "pointer" }}
    >
      {displayPrice}
    </span>
  );
};

const FscLocationListing = () => {
  const { setMessage } = useMessage();
  const [changedPrices, setChangedPrices] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const isInitialMount = useRef(true);

  const {
    checkInputChange,
    handleConfirmNavigation: confirmNavigation,
    handleCancelNavigation: cancelNavigation,
    handleUpdateNavigation,
    showModal,
    isFormChanged,
  } = useFormChangeHandler();

  // Sync changedPrices with Redux prompt slice so sidebar/browser navigation is guarded
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const hasUnsaved = Object.keys(changedPrices).length > 0;
    if (hasUnsaved) {
      checkInputChange();
    } else {
      handleUpdateNavigation();
    }
  }, [changedPrices]);

  // When isFormChanged is cleared externally (e.g. pagination confirm), clear changedPrices too
  useEffect(() => {
    if (!isFormChanged && Object.keys(changedPrices).length > 0) {
      setChangedPrices({});
    }
  }, [isFormChanged]);

  const handlePriceChange = (id: string, newPrice: number | null) => {
    setChangedPrices((prev) => {
      if (newPrice === null) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: newPrice };
    });
  };

  const mapFscLocationData = (data: any[]) =>
    data.map((item: any) => {
      const rawPrice = item?.pricePerGallon ?? 0;
      const displayPrice =
        item?.pricePerGallon !== null && item?.pricePerGallon !== undefined
          ? `$${parseFloat(item.pricePerGallon).toFixed(3)}`
          : "$0.000";

      const row = {
        id: item?.id,
        count: item?.count,
        name: item?.location || "",
        rawPrice,
        displayPrice,
        price: null as any,
      };

      row.price = (
        <InlineEditCell 
        row={row} 
        onPriceChange={handlePriceChange}
        onStartEdit={closeFilter}
         />
      );

      return row;
    });

  const {
    data: apiData,
    loading,
    closeFilter,
    setLoading,
    closeOpenfilterModal,
    isShowFilterModal,
    serviceName,
    setServiceName,
    fetchFilteredData,
    fetchWithCurrentFilters,
    captureAppliedFilters,
    clearCurrentFilters,
    filterApplied,
    setFilterApplied,
  } = useSearchAndFilter<any>({
    apiUrl: GET_FSC_LOCATION_CONFIG,
    limit: DEFAULT_PAGE_SIZE,
    mapUserData: mapFscLocationData,
    screenKey: "fscLocationListing",
  });

  // When data refreshes (e.g. filter apply/clear), discard stale price edits
  useEffect(() => {
    if (Object.keys(changedPrices).length > 0) {
      setChangedPrices({});
    }
  }, [apiData]);

  const handleSave = async () => {
    const updates = Object.entries(changedPrices).map(([id, pricePerGallon]) => ({
      id,
      pricePerGallon
    }));

    if (updates.length === 0) return;

    setSaving(true);
    try {
      const apiResponse = await putApi(UPDATE_FSC_LOCATION_PRICE, updates);
      if (apiResponse?.success) {
        setMessage(
          SERVICE_CONFIG_MESSAGES.FSC_LOCATION_PRICES_UPDATE_SUCCESS,
          "success"
        );
        setChangedPrices({});
        handleUpdateNavigation();
        fetchWithCurrentFilters();
      } else {
        setMessage(apiResponse?.error?.message ?? GENERAL_ERROR_MESSAGE, "error");
      }
    } catch {
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = Object.keys(changedPrices).length > 0;

  const guardUnsavedChanges = useCallback(
    (action: () => void) => {
      if (hasChanges) {
        pendingActionRef.current = action;
        setShowUnsavedModal(true);
      } else {
        action();
      }
    },
    [hasChanges]
  );

  const handleConfirmNavigation = () => {
    setShowUnsavedModal(false);
    setChangedPrices({});
    handleUpdateNavigation();
    fetchWithCurrentFilters();
    if (pendingActionRef.current) {
      pendingActionRef.current();
      pendingActionRef.current = null;
    }
  };

  const handleCancelNavigation = () => {
    setShowUnsavedModal(false);
    pendingActionRef.current = null;
  };

  const handleFilterToggle = () => {
    guardUnsavedChanges(closeOpenfilterModal);
  };

  const data = apiData?.length ? apiData : [];

  if (loading || saving) return <Loader />;

  return (
    <div
      className={`tableWrap ${isShowFilterModal ? "tableWrap--filterOpen" : ""}`}
    >
      <div className="tableWrap__head tableWrap__head--end">
        <div className="btn__group formRow--flexEnd">
          <button
            className="btn btn__primary"
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>

          {filterApplied ? (
            <Badge badgeContent=" " color="primary">
              <button
                className={
                  !isShowFilterModal ? "btn btn__white" : "btn btn__primary"
                }
                onClick={handleFilterToggle}
              >
                {!isShowFilterModal ? <FilterIcon /> : <FilterIconWhite />}
                Filter
              </button>
            </Badge>
          ) : (
            <button
              className={
                !isShowFilterModal ? "btn btn__white" : "btn btn__primary"
              }
              onClick={handleFilterToggle}
            >
              {!isShowFilterModal ? <FilterIcon /> : <FilterIconWhite />}
              Filter
            </button>
          )}

          <FilterSideMenu
            isShowFilterModal={isShowFilterModal}
            closeOpenfilterModal={closeOpenfilterModal}
            isHubHide={true}
            isOrderTypeHide={true}
            isOrderStatusHide={true}
            isRoleHide={true}
            isServiceHide={true}
            isStatusHide={true}
            showCustomerName={false}
            showAgentName={false}
            isAllCustomerTypeHide={true}
            isAgentTypeHide={true}
            showLocationName={true}
            serviceName={serviceName}
            setServiceName={setServiceName}
            fetchFilteredData={fetchFilteredData}
            onFiltersApplied={captureAppliedFilters}
            filterApplied={filterApplied}
            setFilterApplied={setFilterApplied}
            table="FscLocationListingTable"
            clearFiltersFromStorage={clearCurrentFilters}
          />
        </div>
      </div>

      <Table
        table="fscLocationList"
        columns={columns}
        setLoading={setLoading}
        data={data}
        getApiData={fetchWithCurrentFilters}
        editIcon={false}
        manageIcon={false}
        deleteIcon={false}
      />

      {(showUnsavedModal || showModal) && (
        <ConfirmModal
          confirmInfo={{
            heading: "Unsaved Changes!",
            para: "You have not saved your changes, do you want to continue",
          }}
          onConfirm={() => {
            if (showUnsavedModal) {
              handleConfirmNavigation();
            } else {
              setChangedPrices({});
              fetchWithCurrentFilters();
              confirmNavigation();
            }
          }}
          setConfirmModal={() => {
            if (showUnsavedModal) {
              handleCancelNavigation();
            } else {
              cancelNavigation();
            }
          }}
          firstMessageShow={true}
        />
      )}
    </div>
  );
};

export default FscLocationListing;
